import React, { createContext, useState, useContext, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/index.css';
import { Toaster } from '@/components/ui/sonner';

import Landing from '@/pages/Landing';
import Dashboard from '@/pages/Dashboard';
import Chapters from '@/pages/Chapters';
import ChapterView from '@/pages/ChapterView';
import Games from '@/pages/Games';
import Leaderboard from '@/pages/Leaderboard';
import Profile from '@/pages/Profile';

import MatchingGame from '@/games/MatchingGame';
import QuizBattle from '@/games/QuizBattle';
import BlockchainDetective from '@/games/BlockchainDetective';
import BlockchainBuilder from '@/games/BlockchainBuilder';
import SmartContractDebugger from '@/games/SmartContractDebugger';
import HashMiningSimulator from '@/games/HashMiningSimulator';
import TransactionValidator from '@/games/TransactionValidator';
import ChainConnector from '@/games/ChainConnector';
import SmartContractAudit from '@/games/SmartContractAudit';
import ConsensusRace from '@/games/ConsensusRace';
import DeFiCalculator from '@/games/DeFiCalculator';
import WalletSecurityChallenge from '@/games/WalletSecurityChallenge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch profile', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, fetchProfile }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
          <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
          <Route path="/chapters/:level" element={user ? <Chapters /> : <Navigate to="/" />} />
          <Route path="/chapter/:id" element={user ? <ChapterView /> : <Navigate to="/" />} />
          <Route path="/games" element={user ? <Games /> : <Navigate to="/" />} />
          <Route path="/leaderboard" element={user ? <Leaderboard /> : <Navigate to="/" />} />
          <Route path="/profile" element={user ? <Profile /> : <Navigate to="/" />} />
          
          <Route path="/game/matching" element={user ? <MatchingGame /> : <Navigate to="/" />} />
          <Route path="/game/quiz-battle" element={user ? <QuizBattle /> : <Navigate to="/" />} />
          <Route path="/game/detective" element={user ? <BlockchainDetective /> : <Navigate to="/" />} />
          <Route path="/game/builder" element={user ? <BlockchainBuilder /> : <Navigate to="/" />} />
          <Route path="/game/debugger" element={user ? <SmartContractDebugger /> : <Navigate to="/" />} />
          <Route path="/game/mining" element={user ? <HashMiningSimulator /> : <Navigate to="/" />} />
          <Route path="/game/validator" element={user ? <TransactionValidator /> : <Navigate to="/" />} />
          <Route path="/game/connector" element={user ? <ChainConnector /> : <Navigate to="/" />} />
          
          {/* New Games */}
          <Route path="/game/audit" element={user ? <SmartContractAudit /> : <Navigate to="/" />} />
          <Route path="/game/race" element={user ? <ConsensusRace /> : <Navigate to="/" />} />
          <Route path="/game/defi-calculator" element={user ? <DeFiCalculator /> : <Navigate to="/" />} />
          <Route path="/game/security-challenge" element={user ? <WalletSecurityChallenge /> : <Navigate to="/" />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
export { API };
